import React, { useEffect, useState } from 'react';

function getCurrentUserId(): number | null {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId ?? null;
  } catch {
    return null;
  }
}
import { TableSchema } from '../pages/App';
import { apiFetch } from '../api';
import { getPrimaryKey } from '../utils/primaryKey';

interface Props {
  schema: TableSchema;
  record?: Record<string, any> | null;
  onClose: () => void;
  onSaved: (changed: boolean) => void;
}

const RecordModal: React.FC<Props> = ({ schema, record, onClose, onSaved }) => {
  const [form, setForm] = useState<Record<string, any>>({});
  const [references, setReferences] = useState<Record<string, any[]>>({});
  const [monthTouched, setMonthTouched] = useState(false);
  const [isRecordLoading, setIsRecordLoading] = useState(false);

  const editableColumns = schema.columns.filter(c => c.key !== 'id');

  const formatPromoDataValue = (value: any) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };

  useEffect(() => {
    const initial = record ? { ...record } : {};
    if (schema.name === 'users') {
      delete initial['password_hash'];
      initial['password'] = '';
    }
    if (schema.name === 'promo_task' && 'data' in initial) {
      initial['data'] = formatPromoDataValue(initial['data']);
    }
    if (schema.name === 'plan_records') {
      if (record && record['date_month_end']) {
        initial['date_month_end'] = String(record['date_month_end']).slice(0, 7);
      } else if (!record) {
        const now = new Date();
        const m = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        initial['date_month_end'] = m;
        const uid = getCurrentUserId();
        if (uid !== null) initial['owner_id'] = uid;
        initial['created_at'] = now.toISOString();
      }
    }
    setMonthTouched(false);
    setForm(initial);
    editableColumns.forEach(async col => {
      if (col.type === 'reference' && col.refTable) {
        const res = await apiFetch(`/api/${col.refTable}`);
        let data = await res.json();
        if (
          col.refTable === 'articles' &&
          (schema.name === 'plan_records' || schema.name === 'finance_operations') &&
          col.key === 'article_id'
        ) {
          const map = new Map<number | null, any[]>();
          data.forEach((a: any) => {
            const pid = a.parentId ?? a.parent_id ?? null;
            if (!map.has(pid)) map.set(pid, []);
            map.get(pid)!.push(a);
          });
          const sortFn = (a: any, b: any) => {
            const gA = a.groupNumber ?? a.group_number ?? '';
            const gB = b.groupNumber ?? b.group_number ?? '';
            if (gA === gB) {
              const nA = a.number ?? '';
              const nB = b.number ?? '';
              return nA < nB ? -1 : nA > nB ? 1 : 0;
            }
            return gA < gB ? -1 : 1;
          };
          const build = (pid: number | null, level: number): any[] => {
            const items = map.get(pid) || [];
            items.sort(sortFn);
            const res: any[] = [];
            for (const it of items) {
              res.push({ ...it, _level: level });
              res.push(...build(it.id, level + 1));
            }
            return res;
          };
          data = build(null, 0);
        }
        setReferences(prev => ({ ...prev, [col.key]: data }));
      }
    });
  }, [record, schema]);

  useEffect(() => {
    async function loadPromoTaskDetails() {
      if (schema.name !== 'promo_task' || !record) return;
      const primaryKey = getPrimaryKey(schema);
      const recordKey = record[primaryKey] ?? record.id;
      if (!recordKey || record.data !== undefined) return;
      setIsRecordLoading(true);
      try {
        const res = await apiFetch(`/api/promo_task/${recordKey}`);
        if (!res.ok) return;
        const fullRecord = await res.json();
        setForm(current => ({
          ...current,
          ...fullRecord,
          data: formatPromoDataValue(fullRecord.data),
        }));
      } finally {
        setIsRecordLoading(false);
      }
    }
    loadPromoTaskDetails().catch(() => {});
  }, [record, schema]);

  useEffect(() => {
    async function updateMonthDefault() {
      if (record || schema.name !== 'plan_records') return;
      if (!form['article_id'] || monthTouched) return;
      const now = new Date();
      const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const res = await apiFetch(`/api/plan-records?month=${cur}`);
      if (!res.ok) return;
      const existing = await res.json();
      const exists = existing.some(
        (r: any) =>
          String(r.article_id) === String(form['article_id']) &&
          String(r.product_id || '') === String(form['product_id'] || '')
      );
      if (exists) {
        const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const nm = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
        setForm(f => ({ ...f, date_month_end: nm }));
      } else {
        setForm(f => ({ ...f, date_month_end: cur }));
      }
    }
    updateMonthDefault().catch(() => {});
  }, [form['article_id'], form['product_id'], monthTouched]);

  function normalizeComparable(value: any) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return String(value);
    if (value instanceof Date) return value.toISOString();
    return value;
  }

  function hasChanges(payload: Record<string, any>) {
    if (!record) return true;
    if (schema.name === 'users' && payload.password) {
      return true;
    }

    return editableColumns.some(col => {
      if (schema.name === 'users' && col.key === 'password_hash') {
        return false;
      }
      const newValue = normalizeComparable(payload[col.key]);
      const oldValue = normalizeComparable(record[col.key]);
      return String(newValue) !== String(oldValue);
    });
  }

  async function handleSave() {
    const method = record ? 'PUT' : 'POST';
    const primaryKey = getPrimaryKey(schema);
    const recordKey = record ? record[primaryKey] ?? record.id : null;
    const url = record ? `/api/${schema.name}/${recordKey}` : `/api/${schema.name}`;
    const payload = { ...form };
    if (schema.name === 'plan_records' && payload['date_month_end']) {
      const [y, m] = String(payload['date_month_end']).split('-').map(Number);
      const date = `${y}-${String(m).padStart(2, '0')}-01`;
      payload['date_month_end'] = date;
    }
    if (schema.name === 'users') {
      if ('password' in payload) {
        if (!payload.password) {
          delete payload.password;
        }
      }
      delete payload.password_hash;
    }
    if (schema.name === 'promo_task' && typeof payload.data === 'string') {
      const trimmed = payload.data.trim();
      if (!trimmed) {
        payload.data = null;
      } else {
        try {
          payload.data = JSON.parse(trimmed);
        } catch {
          payload.data = trimmed;
        }
      }
    }
    const changed = hasChanges(payload);
    if (!changed) {
      onSaved(false);
      onClose();
      return;
    }
    await apiFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    onSaved(true);
    onClose();
  }

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const confirmed = confirm(
      'Вы уверены, что хотите закрыть окно? Данные не сохранятся.'
    );
    if (confirmed) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div className="w-full max-w-md max-h-[80vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold text-slate-800">
          {record ? 'Редактировать' : 'Добавить'} {schema.label || schema.name}
        </h3>
        {isRecordLoading && (
          <div className="mb-4 flex items-center gap-2 text-xs text-slate-500">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"></span>
            Загружаем данные записи...
          </div>
        )}
        {editableColumns.map(col => (
          <div key={col.key} className="mb-3">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              {col.label}
            </label>
            {col.type === 'reference' && references[col.key] ? (
              <select
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                value={form[col.key] || ''}
                onChange={e => setForm({ ...form, [col.key]: e.target.value })}
                disabled={
                  schema.name === 'plan_records' && col.key === 'owner_id'
                }
              >
                <option value="">--</option>
                {references[col.key].map(r => {
                  const label =
                    schema.name === 'finance_operations' &&
                    (col.key === 'relation_id' || col.key === 'relationId')
                      ? String(r.id)
                      : (schema.name === 'plan_records' || schema.name === 'finance_operations') &&
                        col.key === 'article_id'
                      ? `${r.code ?? ''} ${r.name ?? ''}`.trim()
                      : r.alias && r.alias.trim()
                      ? r.alias
                      : r.name ?? (() => {
                          const fallbackKey = Object.keys(r).find(k => k !== 'id');
                          return fallbackKey ? r[fallbackKey] : '';
                        })();
                  const pad = ' '.repeat((r._level || 0) * 2);
                  return (
                    <option key={r.id} value={r.id}>
                      {pad + label}
                    </option>
                  );
                })}
              </select>
            ) :
            col.type === 'boolean' ||
            (schema.name === 'articles' && (col.key === 'is_income' || col.key === 'isIncome')) ? (
              <input
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                type="checkbox"
                checked={!!form[col.key]}
                onChange={e => setForm({ ...form, [col.key]: e.target.checked })}
              />
            ) : schema.name === 'promo_task' && col.key === 'data' ? (
              <textarea
                className="min-h-[120px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                value={form[col.key] || ''}
                onChange={e => setForm({ ...form, [col.key]: e.target.value })}
              />
            ) : (
              <input
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                type={
                  schema.name === 'users' && col.key === 'password_hash'
                    ? 'password'
                    : schema.name === 'plan_records' &&
                      (col.key === 'date_month_end' || col.key === 'dateMonthEnd')
                    ? 'month'
                    : col.type === 'date'
                    ? 'date'
                    : 'text'
                }
                value={
                  schema.name === 'users' && col.key === 'password_hash'
                    ? form['password'] || ''
                    : form[col.key] || ''
                }
                onChange={e => {
                  if (
                    schema.name === 'plan_records' &&
                    (col.key === 'date_month_end' || col.key === 'dateMonthEnd')
                  ) {
                    setMonthTouched(true);
                  }
                  setForm({
                    ...form,
                    [
                      schema.name === 'users' && col.key === 'password_hash'
                        ? 'password'
                        : col.key
                    ]: e.target.value,
                  });
                }}
                disabled={
                  schema.name === 'plan_records' && col.key === 'created_at'
                }
              />
            )}
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-2">
          <button
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
            onClick={onClose}
          >
            Отмена
          </button>
          <button
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500"
            onClick={handleSave}
          >
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordModal;
